import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../../firebase';

// --- SVG Icons ---
// Using inline SVGs for icons to keep the component self-contained.

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039L38.802 9.92C34.553 6.184 29.654 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.841-5.841C34.553 6.184 29.654 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"></path>
    <path fill="#1976D2" d="M43.611 20.083H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C43.021 36.697 44 34.021 44 31c0-5.239-2.433-9.851-6.389-10.917z"></path>
  </svg>
);

const UserIcon = ({ className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);

const MailIcon = ({ className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
    </svg>
);

const LockIcon = ({ className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);


// --- Reusable Components ---

const InputField = ({ id, type, placeholder, icon, value, onChange }) => (
  <div className="relative mb-4">
    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
      {icon}
    </div>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200"
      placeholder={placeholder}
      required
    />
  </div>
);

const SocialButton = ({ provider, icon, children }) => {
  const handleSocialLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      // User is signed in. The App's onAuthStateChanged will handle the redirect.
    } catch (error) {
      console.error("Google sign-in error", error);
      alert(error.message);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSocialLogin}
      className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200"
    >
      {icon}
      {children}
    </button>
  );
};

const SubmitButton = ({ children }) => (
  <button
    type="submit"
    className="w-full bg-blue-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200"
  >
    {children}
  </button>
);

// --- Form Components ---

const SignInForm = ({ onSwitchMode, onForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // User is signed in. The App's onAuthStateChanged will handle the redirect.
    } catch (err) {
      setError(err.message);
      console.error('Sign in error', err);
    }
  };

  return (
    <div className="animate-fade-in">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Welcome Back!</h2>
      <p className="text-center text-gray-500 mb-8">Sign in to continue to your account.</p>
      <form onSubmit={handleSubmit}>
        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
        <InputField
          id="email"
          type="email"
          placeholder="your@email.com"
          icon={<MailIcon className="w-5 h-5" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputField
          id="password"
          type="password"
          placeholder="Your Password"
          icon={<LockIcon className="w-5 h-5" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="text-right mb-6">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-blue-600 hover:underline"
          >
            Forgot Password?
          </button>
        </div>
        <SubmitButton>Sign In</SubmitButton>
      </form>
      <div className="my-6 flex items-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="mx-4 text-gray-500 text-sm">OR</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <SocialButton provider="Google" icon={<GoogleIcon />}>
        Sign in with Google
      </SocialButton>
      <p className="mt-8 text-center text-sm text-gray-600">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={() => onSwitchMode('signUp')}
          className="font-medium text-blue-600 hover:underline"
        >
          Sign Up
        </button>
      </p>
    </div>
  );
};

const SignUpForm = ({ onSwitchMode }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      alert('Password should be at least 6 characters');
      return;
    }
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // User is signed up and signed in. The App's onAuthStateChanged will handle the redirect.
    } catch (err) {
      setError(err.message);
      console.error('Sign up error', err);
    }
  };

  return (
    <div className="animate-fade-in">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Create an Account</h2>
      <p className="text-center text-gray-500 mb-8">Get started by creating your new account.</p>
      <form onSubmit={handleSubmit}>
        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
        <InputField
          id="name"
          type="text"
          placeholder="Your Name"
          icon={<UserIcon className="w-5 h-5" />}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <InputField
          id="email-signup"
          type="email"
          placeholder="your@email.com"
          icon={<MailIcon className="w-5 h-5" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputField
          id="password-signup"
          type="password"
          placeholder="Create a Password"
          icon={<LockIcon className="w-5 h-5" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="h-6">
          {/* Spacer for layout consistency */}
        </div>
        <SubmitButton>Sign Up</SubmitButton>
      </form>
      <div className="my-6 flex items-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="mx-4 text-gray-500 text-sm">OR</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <SocialButton provider="Google" icon={<GoogleIcon />}>
        Sign up with Google
      </SocialButton>
      <p className="mt-8 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => onSwitchMode('signIn')}
          className="font-medium text-blue-600 hover:underline"
        >
          Sign In
        </button>
      </p>
    </div>
  );
};

const ForgotPasswordForm = ({ onSwitchMode }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset link sent to your email!');
      onSwitchMode('signIn');
    } catch (error) {
      console.error("Password reset error", error);
      alert(error.message);
    }
  };

  return (
    <div className="animate-fade-in">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Forgot Password?</h2>
      <p className="text-center text-gray-500 mb-8">No worries, we'll send you reset instructions.</p>
      <form onSubmit={handleSubmit}>
        <InputField
          id="email-forgot"
          type="email"
          placeholder="your@email.com"
          icon={<MailIcon className="w-5 h-5" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="mt-6">
          <SubmitButton>Send Reset Link</SubmitButton>
        </div>
      </form>
      <p className="mt-8 text-center text-sm text-gray-600">
        <button
          type="button"
          onClick={() => onSwitchMode('signIn')}
          className="font-medium text-blue-600 hover:underline"
        >
          &larr; Back to Sign In
        </button>
      </p>
    </div>
  );
};

// --- Main App Component ---

export default function AuthPage() {
  const [mode, setMode] = useState('signIn'); // 'signIn', 'signUp', or 'forgotPassword'

  const renderForm = () => {
    switch (mode) {
      case 'signUp':
        return <SignUpForm onSwitchMode={setMode} />;
      case 'forgotPassword':
        return <ForgotPasswordForm onSwitchMode={setMode} />;
      case 'signIn':
      default:
        return <SignInForm onSwitchMode={setMode} onForgotPassword={() => setMode('forgotPassword')} />;
    }
  };

  return (
    <div 
      className="min-h-screen font-sans bg-cover bg-center"
      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1554147090-e1221a04a025?q=80&w=2070&auto=format&fit=crop')" }}
      onError={(e) => { e.target.style.backgroundImage = "url('https://placehold.co/600x800/e2e8f0/334155?text=BG+Error&font=sans')" }}
    >
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
      
      <div className="flex min-h-screen">
        {/* Left Panel: Image and Welcome Text (Visible on large screens) */}
        <div 
          className="hidden lg:flex relative w-1/2 bg-cover bg-center items-center justify-center p-12"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1507525428034-b723a9ce6890?q=80&w=2070&auto=format&fit=crop')" }}
          onError={(e) => { e.target.style.backgroundImage = "url('https://placehold.co/1000x1200/334155/ffffff?text=BG+Error&font=sans')" }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-slate-900 bg-opacity-50"></div>
          
          <div className="relative z-10 text-center text-white">
            <h1 className="text-5xl font-bold mb-4 leading-tight">Join Our Community</h1>
            <p className="text-xl max-w-lg">Discover a world of possibilities. Sign in to get started.</p>
          </div>
        </div>

        {/* Right Panel: Authentication Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-gray-100/30 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none">
          <div className="w-full max-w-md">
            <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-2xl p-8 space-y-6">
              {renderForm()}
            </div>
            <footer className="mt-8 text-center text-sm text-gray-100 lg:text-gray-500">
              <p>&copy; {new Date().getFullYear()} kanniur.com. All rights reserved.</p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
